import * as RadixSelect from '@radix-ui/react-select';
import { ChevronDown, Check } from 'lucide-react';
import { Tooltip, InfoIcon } from './Tooltip';

interface SelectOption {
  value: string;
  label: string;
  description?: string;
}

interface SelectProps {
  value: string;
  onChange: (value: string) => void;
  options: SelectOption[];
  label?: string;
  tooltip?: string;
  placeholder?: string;
  id?: string;
}

export function Select({
  value,
  onChange,
  options,
  label,
  tooltip,
  placeholder = 'Select an option…',
  id,
}: SelectProps) {
  const selectedOption = options.find((o) => o.value === value);
  const triggerId = id ?? (label ? label.toLowerCase().replace(/\s+/g, '-') : undefined);

  return (
    <div className="flex flex-col w-full">
      {label && (
        <div className="flex items-center gap-1 mb-1">
          <label htmlFor={triggerId} className="text-[12px] text-azure-muted uppercase tracking-wider font-medium">
            {label}
          </label>
          {tooltip && (
            <Tooltip content={tooltip}>
              <InfoIcon />
            </Tooltip>
          )}
        </div>
      )}

      <RadixSelect.Root value={value} onValueChange={onChange}>
        {/* Trigger: min-h so two-line selected value fits; items-start so description
            appears below the label rather than beside it */}
        <RadixSelect.Trigger
          id={triggerId}
          aria-label={label ?? placeholder}
          className="
            group
            w-full bg-azure-bg border border-azure-border-2 rounded-input
            min-h-[44px] px-3 py-2 text-sm text-azure-text text-left
            focus:outline-none focus:border-azure-blue
            hover:border-azure-muted
            transition-all duration-150
          "
        >
          {/* Custom selected-value display: stacks label + description */}
          <div className="select-stack">
            {selectedOption ? (
              <>
                <span className="select-label">
                  {selectedOption.label}
                </span>
                {selectedOption.description && (
                  <span className="select-desc">
                    {selectedOption.description}
                  </span>
                )}
              </>
            ) : (
              <span className="select-desc">{placeholder}</span>
            )}
          </div>

          <RadixSelect.Icon className="mt-1 flex-shrink-0">
            <ChevronDown className="w-4 h-4 text-azure-muted group-hover:text-azure-text-2 transition-colors" />
          </RadixSelect.Icon>

          {/* RadixSelect.Value hidden but functional for Radix state */}
          <RadixSelect.Value aria-hidden className="hidden" />
        </RadixSelect.Trigger>

        <RadixSelect.Portal>
          <RadixSelect.Content
            className="
              z-[100] overflow-hidden
              rounded-[4px] max-h-[280px] shadow-none
            "
            style={{ backgroundColor: 'var(--surface-container-high)', border: '1px solid var(--outline-variant)' }}
            position="popper"
            sideOffset={4}
          >
            <RadixSelect.Viewport className="p-1">
              {options.map((opt) => (
                <RadixSelect.Item
                  key={opt.value}
                  value={opt.value}
                  className="
                    relative flex flex-col justify-center min-h-[44px] px-8 py-2.5
                    text-[14px] text-azure-text-2 rounded-[2px]
                    focus:bg-azure-blue-70 focus:text-azure-blue-10 focus:outline-none
                    hover:opacity-80
                    data-[state=checked]:bg-[var(--surface-container-high)] data-[state=checked]:text-[var(--text-primary)]
                    cursor-pointer select-none
                  "
                >
                  <RadixSelect.ItemText>
                    <div className="select-stack py-0.5">
                      <span className="select-label font-medium">
                        {opt.label}
                      </span>
                      {opt.description && (
                        <span className="select-desc">
                          {opt.description}
                        </span>
                      )}
                    </div>
                  </RadixSelect.ItemText>
                  <RadixSelect.ItemIndicator className="absolute left-2 inline-flex items-center">
                    <Check className="w-4 h-4" />
                  </RadixSelect.ItemIndicator>
                </RadixSelect.Item>
              ))}
            </RadixSelect.Viewport>
          </RadixSelect.Content>
        </RadixSelect.Portal>
      </RadixSelect.Root>
    </div>
  );
}
